import * as React from 'react';
import {
  Box,
  Divider,
  Grid,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import Alert from '@material-ui/lab/Alert';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';

import useEventListener from '../../lib/useEventListener';
import deselectElementPreventDefaultAndBlur from '../../lib/deselectAndBlur';
import EstimatorCommentCards from '../estimator/EstimatorCommentCards';
import JewelerSidebar from './JewelerSidebar';
import JewelerRepairItemDisplay from './JewelerRepairItemDisplay';
import type { IScannedRepair } from '../marking/MarkingInterfaces';
import RepairCardCarousel from '../metalCollection/RepairCardCarousel';
import RejectWorkDialog from '../production/RejectWorkDialog';
import ScanOutDialog from './ScanOutDialog';

interface SidebarRepair {
  barcode: string;
  secondaryText: string;
  status: 'shipLog' | 'scheduled' ;
  secondaryStatusStart: Date | null;
}

const Jeweler: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedBarcode, setSelectedBarcode] = React.useState<string>();
  const [repairId, setRepairId] = React.useState<undefined | string>();
  const [showRejectWorkDialog, setShowRejectWorkDialog] = React.useState<boolean>(false);
  const [showScanOutDialog, setShowScanOutDialog] = React.useState<boolean>(false);

  const getRepairId = (repairBarcodeParam: string) => {
    axios.get<{ repairId:string }>('/Jeweler/ScanRepair', {
      params: {
        barcode: repairBarcodeParam,
      },
    })
      .then((response) => { setRepairId(response.data.repairId); return response.data.repairId; })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const binRepairFetcher = (url: string) => axios.get<{ repairs:SidebarRepair[] }>(url).then((res) => res.data.repairs)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const { data: binRepairs, mutate } = useSWR('/jeweler/GetBinRepairs', binRepairFetcher);

  const updateSelectedRepair = (repairBarcodeParam: string) => {
    if (repairBarcodeParam && binRepairs?.find((r) => r.barcode === repairBarcodeParam)) {
      if (binRepairs?.filter((r) => r.status === 'shipLog').length === 0
        || binRepairs?.filter((r) => r.status === 'shipLog')?.findIndex((r) => r.barcode === repairBarcodeParam) !== -1
        || (binRepairs?.find((r) => r.barcode === repairBarcodeParam && r.status !== 'shipLog' && !r.secondaryStatusStart)
          && binRepairs?.filter((r) => r.status === 'shipLog' && !r.secondaryStatusStart).length === 0)
      ) {
        getRepairId(repairBarcodeParam);
        setSelectedBarcode(repairBarcodeParam);
      } else {
        enqueueSnackbar('Please scan ship log items first', { persist: false, variant: 'error' });
      }
    } else {
      enqueueSnackbar(`Scanned barcode: ${repairBarcodeParam} doesn't exists in user's bin.`, { persist: false, variant: 'error' });
    }
  };

  const repairFetcher = (url: string, repairIdParam: string) => axios.get<IScannedRepair>(url, {
    params: {
      repairId: repairIdParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const { data } = useSWR(repairId ? ['/Jeweler/GetRepair', repairId] : null, (url, repairIdParam) => repairFetcher(url, repairIdParam));

  const handleOutScan = (scannedBarcode: string | '') => {
    axios.post('/scanner/LogManualScan', {
      ScanType: 'Out',
      ScanBody: scannedBarcode,
      Location: '',
    })
      .catch((er: AxiosError) => {
        enqueueSnackbar(er?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const startSoak = () => {
    setShowScanOutDialog(false);
    axios.post<string>('/jeweler/startSoak', {
      barcode: selectedBarcode,
    }).then(() => {
      mutate().catch((e: AxiosError) => { enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' }); });
      setRepairId(undefined);
      setSelectedBarcode(undefined);
    }).catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });
  };

  const completeWork = () => {
    setShowRejectWorkDialog(false);
    axios.post<string>('/jeweler/completeWork', {
      barcode: selectedBarcode,
    }).then(() => {
      mutate().catch((e: AxiosError) => { enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' }); });
      setRepairId(undefined);
      setSelectedBarcode(undefined);
    }).catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });
  };

  const rejectWork = (rejectReason: string[]) => {
    setShowRejectWorkDialog(false);
    axios.post('/jeweler/rejectWork', {
      barcode: selectedBarcode,
      RejectReasons: rejectReason,
    })
      .then(() => {
        setRepairId(undefined);
        setSelectedBarcode(undefined);
        mutate().catch((e: AxiosError) => { enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' }); });
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      });
  };

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        deselectElementPreventDefaultAndBlur(e);
        setShowScanOutDialog(false);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        deselectElementPreventDefaultAndBlur(e);
        updateSelectedRepair(barcodeBuffer.replace('{*|}', ''));
        setShowScanOutDialog(false);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('out:')) {
        deselectElementPreventDefaultAndBlur(e);
        setShowScanOutDialog(true);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('<>??<>')) {
        deselectElementPreventDefaultAndBlur(e);
        setShowScanOutDialog(true);
        handleOutScan(barcodeBuffer.replace('<>??<>', '') || '');
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  return (
    <Grid container spacing={1}>
      <Grid item xs={2} style={{ minWidth: '15rem' }}>
        <JewelerSidebar
          sidebarRepairs={binRepairs || []}
          repairItemOnClick={process.env.REACT_APP_ENV !== 'local' ? () => {} : updateSelectedRepair}
          selectedBarcode={selectedBarcode}
          secondaryStatusName="Soaking"
        />
      </Grid>
      <Grid item xs={10}>
        <Box px={1}>
          <Divider variant="fullWidth" orientation="vertical" />
        </Box>
        <Grid container item spacing={2} sm={12} md={11}>
          <Grid item xs={12}>
            <Alert
              severity="info"
            >
              {repairId ? 'Review images, repair information, and measurements before starting soak.' : 'Scan in repair item to start the jewelry repair process.'}
            </Alert>
          </Grid>
          {repairId && (
          <>
            <Grid item xs={12}>
              <RepairCardCarousel
                key={selectedBarcode}
                barcode={selectedBarcode || ''}
                secondaryText={
                  `${data?.repairItems?.length || ''} item${data?.repairItems?.length === 1 ? '' : 's'}`
                }
              />
            </Grid>
            <Grid item xs={12}>
              <JewelerRepairItemDisplay repairItems={data?.repairItems || []} />
            </Grid>
            <Grid item xs={12}>
              <Box p={2} />
            </Grid>
            <Grid item xs={12}>
              <EstimatorCommentCards
                repairId={repairId}
                repairTableLength={0}
                allowNewComments={false}
                externalCommentsOnly
              />
            </Grid>
            <Grid item xs={12} />
            <RejectWorkDialog
              barcode={selectedBarcode || ''}
              showModal={showRejectWorkDialog}
              onClose={() => setShowRejectWorkDialog(false)}
              rejectWork={rejectWork}
              station="jeweler"
            />
            <ScanOutDialog
              barcode={selectedBarcode || ''}
              showModal={showScanOutDialog}
              onClose={() => setShowScanOutDialog(false)}
              startSoaking={startSoak}
              showRejectReasonDialog={setShowRejectWorkDialog}
              workComplete={completeWork}
              isJeweler
            />
          </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Jeweler;
